import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import Seo from "../components/Seo";
import { KineticLines, Reveal, SectionLabel } from "../components/Reveal";
import FinalCTA from "../components/home/FinalCTA";
import { ArrowRight, Quote } from "lucide-react";

const IMG_STORY =
  "https://images.unsplash.com/photo-1613186145425-5bb4eca455d7?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1200";
const IMG_TEAM =
  "https://images.unsplash.com/photo-1758691736097-7f735ac5f118?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1000";

const TIMELINE = [
  { year: "2001", title: "The first chapter", copy: "The company started helping businesses in 2001 — attracting customers and growing sales for owners who needed both." },
  { year: "2014", title: "Community Promotions", copy: "The mission continued under the Community Promotions name, deepening relationships with owners and decision-makers across the country." },
  { year: "Today", title: "Bottom Line Improvements", copy: "Through those relationships, a pattern emerged: improving profits involves more than driving new revenue. Owners were struggling with rising operating expenses, technology costs, healthcare expenses, and overlooked opportunities. Bottom Line Improvements brings useful specialist resources into one accessible experience." },
];

export default function About() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <main data-testid="page-about">
      <Seo
        title="About Us"
        description="We've helped businesses grow for over 25 years. Now we're helping them keep more of what they earn — as a division of Community Promotions."
        path="/about"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-ice pt-28 lg:pt-36 pb-16 md:pb-24">
        <div className="absolute inset-0 bg-arch" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <SectionLabel>About Bottom Line Improvements</SectionLabel>
            <KineticLines
              as="h1"
              className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl sm:text-5xl leading-[1.1]"
              lines={[
                "We've Helped Businesses Grow for Over 25 Years.",
                <>Now We're Helping Them <span className="text-teal-dark">Keep More of What They Earn.</span></>,
              ]}
            />
          </div>
        </div>
      </section>

      {/* Story with image */}
      <section ref={ref} className="py-16 md:py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <SectionLabel>How We Got Here</SectionLabel>
            <div className="mt-6 space-y-5 text-base md:text-lg leading-relaxed text-slate">
              <Reveal><p>
                The company started helping businesses in 2001. Community Promotions continued developing
                relationships with owners and decision-makers year after year.
              </p></Reveal>
              <Reveal><p>
                Through those relationships, the team recognized something that rarely made it into sales
                conversations: improving profits involves more than driving new revenue.
              </p></Reveal>
              <Reveal><p>
                Business owners frequently struggle with increasing operating expenses, technology costs, healthcare
                expenses, and overlooked opportunities. Bottom Line Improvements brings useful specialist resources
                into one accessible experience.
              </p></Reveal>
            </div>
            <Reveal delay={0.1}>
              <div className="mt-8 relative rounded-2xl border-l-4 border-teal bg-ice/70 p-6 md:p-7" data-testid="about-central-message">
                <Quote className="w-6 h-6 text-teal-dark mb-3" />
                <p className="font-display font-bold tracking-tight text-navy text-lg md:text-xl leading-snug">
                  We believe helping businesses succeed means sharing useful opportunities — not just selling
                  services.
                </p>
              </div>
            </Reveal>
          </div>
          <motion.div style={reduced ? {} : { y: yImg }} className="relative">
            <div className="rounded-3xl overflow-hidden shadow-lift">
              <img src={IMG_STORY} alt="Business advisor working closely with a client over documents" loading="lazy" className="w-full h-[380px] md:h-[480px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-4 sm:left-6 bg-navy text-white rounded-2xl px-6 py-4 shadow-lift rotate-[-2deg]">
              <p className="font-display font-extrabold text-xl text-teal-bright">2001 — Today</p>
              <p className="text-[11px] font-semibold uppercase tracking-wider2 text-ice/70 mt-0.5">A division of Community Promotions</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Animated history timeline */}
      <section className="py-16 md:py-24 bg-ice/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <SectionLabel>The Timeline</SectionLabel>
            <KineticLines as="h2" className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl sm:text-4xl leading-[1.1]" lines={["Three chapters. One mission."]} />
          </div>
          <div className="mt-12 relative">
            <div className="absolute left-[15px] md:left-1/2 top-0 bottom-0 w-[2px] bg-line md:-translate-x-1/2" aria-hidden="true" />
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={i * 0.06}>
                <div className={`relative flex md:w-1/2 pb-12 last:pb-0 ${i % 2 === 0 ? "md:pr-12" : "md:ml-auto md:pl-12"} pl-12 md:pl-0 ${i % 2 === 0 ? "" : "md:pl-12"}`}>
                  <span className={`absolute left-0 md:left-auto top-0 ${i % 2 === 0 ? "md:-right-[18px]" : "md:-left-[18px]"} w-9 h-9 rounded-full bg-navy border-4 border-ice text-teal-bright text-[10px] font-extrabold flex items-center justify-center`} aria-hidden="true">
                    {t.year.slice(2)}
                  </span>
                  <div className="rounded-2xl bg-white border border-line shadow-card p-6 md:p-7">
                    <p className="text-teal-dark font-display font-extrabold">{t.year}</p>
                    <h3 className="mt-1 font-display font-extrabold tracking-tight text-navy text-lg">{t.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate">{t.copy}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Reserved team photography slot */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionLabel>The People Behind The Work</SectionLabel>
            <KineticLines as="h2" className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl sm:text-4xl leading-[1.1]" lines={["Real relationships, built over real transactions."]} />
            <Reveal delay={0.1}>
              <p className="mt-5 text-base leading-relaxed text-slate">
                This business runs on personal relationships — owners who call when something changes, and a team
                that answers. We're preparing a proper introduction.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border-2 border-dashed border-royal/30 bg-ice/40 p-8 md:p-12 text-center" data-testid="about-team-photography-slot">
              <img src={IMG_TEAM} alt="Space reserved for founder and team photography" loading="lazy" className="w-full h-[260px] md:h-[320px] object-cover rounded-2xl opacity-90" />
              <p className="mt-5 text-xs font-bold uppercase tracking-wider2 text-royal">
                Founder & team photography — reserved, coming soon
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <FinalCTA />
    </main>
  );
}
